import { HorizontalBarItem } from "@common/components/HorizontalBar/HorizontalBar";
import { NetworkHealthEndpoint, NetworkHealthStatusDistribution } from "@common/models/Analytics.model";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { toHorizontalBarItems } from "@main/pages/Release/helpers/endpointStatusColors";
import { fetchAllPages } from "@main/pages/Release/helpers/fetchAllPages";
import { useAnalyticsDeps } from "@main/pages/Release/Release";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

export interface EndpointHealthItem {
  score: number | undefined | null;
  method: string;
  url: string;
  isError: boolean;
  barItems: HorizontalBarItem[];
  hasWarning: boolean;
}

const hasWarning = (data: NetworkHealthStatusDistribution[]): boolean =>
  data.some((d) => {
    const prefix = Number(d.status_class.charAt(0));
    return (prefix === 4 || prefix === 5) && d.percentage > 20;
  });

const isEndpointError = (data: NetworkHealthStatusDistribution[]): boolean =>
  data
    .filter(
      (s) =>
        s.status_class.startsWith("4") ||
        s.status_class.startsWith("5") ||
        s.status_class.startsWith("err") ||
        s.status_class.startsWith("t/o"),
    )
    .reduce((sum, d) => sum + d.percentage, 0) > 50;

export default function useEndpointHealthData() {
  const [searchParams] = useSearchParams();
  const selectedVersion = searchParams.get("version") ?? "";

  const { selectedWorkspace, selectedApp } = useAppSelectionContext();
  const { analyticsService } = useAnalyticsDeps();

  async function getEndpointsHealthDetails(signal?: AbortSignal): Promise<NetworkHealthEndpoint[] | []> {
    if (!selectedWorkspace || !selectedApp || !selectedVersion) return [];

    return fetchAllPages(100, (offset, limit) =>
      analyticsService
        .getNetworkHealthDetails(selectedWorkspace.id, selectedApp.id, selectedVersion, offset, limit, signal)
        .then(({ data }) => ({ items: data.items ?? [], total: data.total })),
    );
  }

  const { data: endpointsHealthDetailsData, isLoading } = useQuery(
    ["networkHealth", { selectedApp, selectedVersion }],
    ({ signal }) => getEndpointsHealthDetails(signal),
    {
      enabled: Boolean(selectedVersion),
      refetchOnWindowFocus: false,
      cacheTime: Infinity,
    },
  );

  const endpoints: EndpointHealthItem[] | undefined = (
    endpointsHealthDetailsData as NetworkHealthEndpoint[] | undefined
  )?.map((endpoint): EndpointHealthItem => {
    const distribution = endpoint.status_distribution ?? [];
    const isError = isEndpointError(distribution);

    return {
      score: endpoint.score,
      method: endpoint.method,
      url: endpoint.endpoint,
      isError,
      barItems: toHorizontalBarItems(distribution, isError),
      hasWarning: !isError && hasWarning(distribution),
    };
  });

  return {
    endpoints,
    isLoading,
  };
}
